/**
 * Supplier Detail Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useSupplierDetail hook.
 */

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Loader2,
  ArrowLeft,
  Star,
  Package,
  Mail,
  Power,
  Camera,
  ShieldAlert,
  ShieldCheck,
  Shield,
  ShieldX,
  Award,
  Info,
} from 'lucide-react';
import { getRiskLevelStyle } from './suppliers-list/utils';
import { getGradeInfo, ratingBadgeStyle, bandRange } from './supplier-performance/utils';
import { useRatingBands } from '../hooks/useRatingBands';
import { suppliersAPI, coreAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../lib/api/types/common';
import { useFormatters } from '../contexts/PreferencesContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { toast } from 'sonner';

// Import modular components
import {
  useSupplierDetail,
  OverviewTab,
  PerformanceTab,
  OrdersTab,
  ContractsTab,
  RiskTab,
  QualityTab,
  UsersTab,
  UserModal,
  EditUserDialog,
  ActivityTab,
  CreditScoreTab,
  QuestionnairesTab,
  SettingsTab,
  DocumentsTab,
  ApprovalsTab,
  CalendarTab,
} from './supplier-detail';
import GroupStructureTab from './supplier-detail/GroupStructureTab';

export default function SupplierDetail() {
  // The active/inactive toggle is staff/superuser only server-side (a
  // deactivation drops the supplier's POs out of the ERP feed, so it is
  // restricted and audited). Don't offer a button the user cannot use.
  const { user: authUser } = useAuth();
  const canToggleActive = !!authUser?.is_staff || !!authUser?.is_superuser;
  const navigate = useNavigate();
  const { formatCurrency: formatCurrencyFromHook } = useFormatters();
  const { supplierProfileLayout, isSupplierCardFieldEnabled } = useMenuSettings();
  // Customer review 11 Sep (item 2): the configured Grade Thresholds.
  const { bands: ratingBands } = useRatingBands();

  const {
    // State
    supplier,
    companyInfo,
    orders,
    ordersPage,
    ordersCount,
    ordersPageSize,
    ordersSupplierCode,
    fetchOrders,
    setOrdersSupplierCode,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    impersonating,
    otifSettings,
    otifLoading,
    otifSaving,
    // CR-5857
    editUser,
    editUserOpen,

    // Actions
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleOtifSettingsUpdate,
    setOtifSettings,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,
    fetchSupplierData,
    fetchUsers,
    // CR-5857
    handleOpenEditUser,
    handleCloseEditUser,
    handleEditUserSaved,
    handleResetPassword,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isCustomerAdmin,
    isOwnCompany,
    userSearch,
  } = useSupplierDetail();

  const [toggling, setToggling] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const canEditLogo = isHelixAdmin || isCustomer || isOwnCompany;

  const onLogoFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setUploadingLogo(true);
      await handleUploadLogo(file);
      toast.success('Logo updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Format currency helper
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return formatCurrencyFromHook(num);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Supplier not found'}</p>
          <Button onClick={() => navigate(-1)} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/suppliers')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {(() => {
            const logo = (supplier.company_details as unknown as Record<string, unknown>)?.['logo_url'] as string | undefined;
            return (
              <div className="relative w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0 p-2 group">
                {logo ? (
                  <img src={logo} alt={supplier.company_name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-3xl font-semibold text-muted-foreground">
                    {supplier.company_name.trim().charAt(0).toUpperCase() || '?'}
                  </span>
                )}
                {canEditLogo && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onLogoFileChosen}
                    />
                    <button
                      type="button"
                      aria-label="Change company logo"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 text-white opacity-0 group-hover:opacity-100 transition disabled:opacity-60"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <Camera className="h-5 w-5 mr-1" />
                          <span className="text-xs font-medium">Change</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            );
          })()}
          <div>
            <div className="flex items-center gap-3 mb-2">
              {supplier.is_preferred && (
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              )}
              <h1 className="text-3xl font-bold tracking-tight">{supplier.company_name}</h1>
              <Badge
                className={
                  (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'approved' || supplier.is_approved
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'not_approved'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'pending_initial'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'invited'
                    ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                    : (supplier as unknown as Record<string, unknown>)['onboarding_status'] === 'due_diligence'
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                }
              >
                {(() => {
                  const status = (supplier as unknown as Record<string, unknown>)['onboarding_status'] as string;
                  switch (status) {
                    case 'approved': return 'Approved';
                    case 'not_approved': return 'Not Approved';
                    case 'pending_initial': return 'Pending Initial Approval';
                    case 'invited': return 'Invited';
                    case 'due_diligence': return 'Due Diligence';
                    case 'draft': return 'Draft';
                    default: return supplier.is_approved ? 'Approved' : 'Under Review';
                  }
                })()}
              </Badge>
              {/* The approval level chosen when the supplier was approved — the
                  answer to "what is this supplier approved to?" after the
                  Level 1/2/3 dialog closes. */}
              {supplier.approval_level && (
                <Badge
                  variant="outline"
                  className="text-xs font-medium px-2.5 py-1 border-green-300 text-green-800 dark:text-green-200"
                  title={supplier.approved_at ? `Approved on ${new Date(supplier.approved_at).toLocaleDateString()}` : undefined}
                >
                  {supplier.approval_level_display || `Level ${supplier.approval_level}`}
                </Badge>
              )}
              {/* CR-4771: Risk Level badge — same styling as the cards */}
              {supplier.risk_level && supplier.risk_level !== 'none' && (() => {
                const style = getRiskLevelStyle(supplier.risk_level);
                const Icon = style.icon === 'critical' ? ShieldX
                  : style.icon === 'high' ? ShieldAlert
                  : style.icon === 'low' ? ShieldCheck
                  : Shield;
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${style.badgeColor}`}>
                    <Icon className="h-3.5 w-3.5 mr-1" />
                    {style.label}
                  </Badge>
                );
              })()}
              {/* CR-4771: Credit Score badge — banding matches SupplierCard */}
              {typeof supplier.latest_credit_score === 'number' && (() => {
                const score = supplier.latest_credit_score;
                const band = score >= 800
                  ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                  : score >= 700
                    ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                    : score >= 600
                      ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                      : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
                return (
                  <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                    Credit {score}
                  </Badge>
                );
              })()}
              {/* CR-4056: Active/Inactive Toggle — staff/superuser only; others see the status. */}
              {canToggleActive ? (
              <button
                onClick={async () => {
                  try {
                    setToggling(true);
                    const newState = !supplier.company_details.is_active;
                    await suppliersAPI.toggleSupplierActive(supplier.company, newState);
                    toast.success(`Supplier ${newState ? 'activated' : 'deactivated'} successfully`);
                    await fetchSupplierData();
                  } catch (err: unknown) {
                    // The API envelope carries `message`, not DRF's `detail`;
                    // reading the wrong key hid "Only staff or superusers can
                    // activate or deactivate a supplier" behind a generic toast.
                    toast.error(getApiErrorMessage(err, 'Failed to update supplier status'));
                  } finally {
                    setToggling(false);
                  }
                }}
                disabled={toggling}
                className={`ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                  supplier.company_details.is_active
                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                }`}
                title={`Click to ${supplier.company_details.is_active ? 'deactivate' : 'activate'} supplier`}
              >
                {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                {supplier.company_details.is_active ? 'Active' : 'Inactive'}
              </button>
              ) : (
                <span
                  className={`ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    supplier.company_details.is_active
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                  title="Only Valuechain staff can activate or deactivate a supplier"
                >
                  <Power className="h-3 w-3" />
                  {supplier.company_details.is_active ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              {supplier.company_details.company_type === 'supplier' ? 'Supplier' : 'Company'} Profile
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              // Named contact first, then the company email, then the
              // supplier's own users — the Users tab below listed Jessie
              // Pinkman with an address while this button said there was no
              // contact, because it only ever read company.primary_email.
              let email = supplier.primary_contact?.email || supplier.company_details.primary_email || '';
              if (!email) {
                try {
                  const res = await coreAPI.getCompanyUsers(supplier.company);
                  const users = (res.data || []) as Array<{ email?: string; is_active?: boolean; user?: { email?: string; is_active?: boolean } }>;
                  const first = users.find((u) => (u.is_active ?? u.user?.is_active ?? true) && (u.email || u.user?.email));
                  email = first?.email || first?.user?.email || '';
                } catch (err: unknown) {
                  console.error('Error loading supplier users for contact:', err);
                }
              }
              if (email) {
                window.open(`mailto:${email}`, '_blank');
              } else {
                toast.error('No contact email available for this supplier — add a user or a primary contact first.');
              }
            }}
          >
            <Mail className="h-4 w-4 mr-2" />
            Contact
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      {/* CR-5767: Replaced OTD KPI with Performance Rating */}
      {(supplierProfileLayout.stats.otd || supplierProfileLayout.stats.total_orders) && (
        <div className={`grid gap-4 ${supplierProfileLayout.stats.otd && supplierProfileLayout.stats.total_orders ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          {/* CR-5767: Performance Rating replaces OTD */}
          {supplierProfileLayout.stats.otd && (() => {
            const score = supplier.combined_score;
            const hasScore = typeof score === 'number' && !isNaN(score);
            const gradeInfo = getGradeInfo(score, ratingBands);
            return (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Performance Rating
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs text-xs p-3">
                          <p className="font-semibold mb-1">Rating Calculation</p>
                          <p className="mb-2">Combined Score = (OTIF Score × 70%) + (PPM Score × 30%)</p>
                          <p className="font-semibold mb-1">Rating Tiers</p>
                          {/* Customer review 11 Sep (msg 2): the configured bands,
                              not a hard-coded Gold/Silver/Bronze list. */}
                          <ul className="space-y-0.5">
                            {ratingBands.map((b) => (
                              <li key={b.id}>• {b.display_label || b.label || b.grade}: {bandRange(b)}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hasScore ? (
                    <Badge
                      className="text-xl font-bold px-3 py-1"
                      style={{
                        ...ratingBadgeStyle(gradeInfo.color),
                      }}
                    >
                      {gradeInfo.label}
                    </Badge>
                  ) : (
                    <div className="text-3xl font-bold text-muted-foreground">N/A</div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Combined Score: {hasScore ? score.toFixed(1) : '-'}
                  </p>
                </CardContent>
              </Card>
            );
          })()}
          {supplierProfileLayout.stats.total_orders && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Total Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{supplier.total_orders}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {supplier.completed_orders} completed
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <TabsTrigger value="overview">Company Overview</TabsTrigger>
          {supplierProfileLayout.tabs.performance && <TabsTrigger value="performance">Performance</TabsTrigger>}
          {supplierProfileLayout.tabs.orders && <TabsTrigger value="orders">Orders</TabsTrigger>}
          {supplierProfileLayout.tabs.contracts && <TabsTrigger value="contracts">Contracts</TabsTrigger>}
          {/* Risk matrix (Phase 3): follows the supplier-card risk badge toggle. */}
          {isSupplierCardFieldEnabled('risk_badge') && <TabsTrigger value="risk">Risk</TabsTrigger>}
          {supplierProfileLayout.tabs.quality && <TabsTrigger value="quality">Quality</TabsTrigger>}
          {supplierProfileLayout.tabs.questionnaires && <TabsTrigger value="questionnaires">Questionnaires</TabsTrigger>}
          {supplierProfileLayout.tabs.credit_scores && <TabsTrigger value="credit-scores">Credit Scores</TabsTrigger>}
          {supplierProfileLayout.tabs.users && <TabsTrigger value="users">Users</TabsTrigger>}
          {/* Defaults to visible when an existing tenant's layout JSON
              predates the documents key. Bracket access because documents
              isn't an explicit field on SupplierProfileLayout.tabs. */}
          {(supplierProfileLayout.tabs['documents'] ?? true) && <TabsTrigger value="documents">Documents</TabsTrigger>}
          {/* Approvals tab - displays supplier certifications/accreditations */}
          {(supplierProfileLayout.tabs['approvals'] ?? true) && <TabsTrigger value="approvals">Approvals</TabsTrigger>}
          {/* Calendar tab - displays supplier-specific calendar events */}
          {(supplierProfileLayout.tabs['calendar'] ?? true) && <TabsTrigger value="calendar">Calendar</TabsTrigger>}
          {/* CR-4773: Renamed to match the card content ("Communication Audit Log") */}
          {supplierProfileLayout.tabs.activity_log && <TabsTrigger value="activity">Communication Log</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
          {supplierProfileLayout.tabs.group_structure && <TabsTrigger value="group-structure">Group Structure</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <OverviewTab
            supplier={supplier}
            companyInfo={companyInfo}
            canEdit={isHelixAdmin || isCustomer}
            onUpdateProfile={handleUpdateProfile}
            onUpdateCompany={handleUpdateCompany}
          />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <PerformanceTab supplier={supplier} />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <OrdersTab
            supplier={supplier}
            orders={orders}
            ordersLoading={ordersLoading}
            formatCurrency={formatCurrency}
            ordersPage={ordersPage}
            ordersCount={ordersCount}
            ordersPageSize={ordersPageSize}
            ordersSupplierCode={ordersSupplierCode}
            fetchOrders={fetchOrders}
            setOrdersSupplierCode={setOrdersSupplierCode}
          />
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <ContractsTab supplier={supplier} />
        </TabsContent>

        {/* Risk Tab */}
        <TabsContent value="risk" className="space-y-4">
          <RiskTab supplier={supplier} />
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <QualityTab supplier={supplier} ncrs={ncrs} ncrsLoading={ncrsLoading} />
        </TabsContent>

        {/* Questionnaires Tab */}
        <TabsContent value="questionnaires" className="space-y-4">
          <QuestionnairesTab supplierId={supplier.company} supplierName={supplier.company_name} />
        </TabsContent>

        {/* Credit Scores Tab */}
        <TabsContent value="credit-scores" className="space-y-4">
          <CreditScoreTab supplier={supplier} />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <UsersTab
            users={users}
            filteredUsers={filteredUsers}
            usersLoading={usersLoading}
            userSearch={userSearch}
            impersonating={impersonating}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            isCustomerAdmin={isCustomerAdmin}
            companyId={supplier?.company || 0}
            companyName={supplier?.company_name || ''}
            groupId={supplier?.company_details?.group}
            onUserSearchChange={setUserSearch}
            onOpenUserModal={handleOpenUserModal}
            onImpersonate={handleImpersonate}
            onRefreshUsers={fetchUsers}
            onEditUser={handleOpenEditUser}
            onResetPassword={handleResetPassword}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <DocumentsTab supplier={supplier} />
        </TabsContent>

        {/* Approvals Tab - Supplier certifications and accreditations */}
        <TabsContent value="approvals" className="space-y-4">
          <ApprovalsTab companyId={supplier.company} />
        </TabsContent>

        {/* Calendar Tab - Supplier-specific calendar events */}
        <TabsContent value="calendar" className="space-y-4">
          <CalendarTab supplier={supplier} onTabChange={setActiveTab} />
        </TabsContent>

        {/* CR-4477: Activity / Traceability Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <ActivityTab supplier={supplier} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <SettingsTab
            supplier={supplier}
            otifSettings={otifSettings}
            otifLoading={otifLoading}
            otifSaving={otifSaving}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
            isCustomerAdmin={isCustomerAdmin}
            onOtifSettingsUpdate={handleOtifSettingsUpdate}
            setOtifSettings={setOtifSettings}
            onUpdateProfile={handleUpdateProfile}
          />
        </TabsContent>

        {/* Group Structure Tab */}
        <TabsContent value="group-structure" className="space-y-4">
          <GroupStructureTab
            supplier={supplier}
            isHelixAdmin={isHelixAdmin}
            isCustomer={isCustomer}
          />
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      <UserModal
        open={userModalOpen}
        onOpenChange={handleCloseUserModal}
        selectedUser={selectedUser}
        userModalTab={userModalTab}
        onTabChange={setUserModalTab}
        userSitePermissions={userSitePermissions}
        permissionsLoading={permissionsLoading}
        savingPermission={savingPermission}
        impersonating={impersonating}
        isHelixAdmin={isHelixAdmin}
        isCustomer={isCustomer}
        isCustomerAdmin={isCustomerAdmin}
        contextCompanyName={supplier?.company_name}
        onToggleSiteLink={handleToggleSiteLink}
        onUpdatePermission={handleUpdatePermission}
        onImpersonate={handleImpersonate}
        onEditUser={handleOpenEditUser}
        onResetPassword={handleResetPassword}
      />

      {/* CR-5857: Edit Profile dialog */}
      <EditUserDialog
        open={editUserOpen}
        onOpenChange={(open) => { if (!open) handleCloseEditUser(); }}
        userProfile={editUser}
        onSaved={handleEditUserSaved}
        companyId={supplier?.company || 0}
        companyName={supplier?.company_name || ''}
      />
    </div>
  );
}
